import React from "react";
import { useAuth as useClerkAuth, RedirectToSignIn, useUser } from "@clerk/clerk-react";
import { useAuth as useLocalAuth } from "../../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import ModernLoader from "./ModernLoader";

const ClerkProtectedRoute = ({ children, adminOnly = false, partnerOnly = false }) => {
  const { isLoaded, isSignedIn } = useClerkAuth();
  const { user: clerkUser } = useUser();
  const { user: dbUser, loading: dbLoading } = useLocalAuth();
  const navigate = useNavigate();
  
  // CRITICAL: Exit early if we are on the onboarding page to prevent redirection loops
  const currentPath = window.location.pathname.toLowerCase();
  if (currentPath.startsWith("/partner/onboarding") || currentPath.includes("onboarding")) {
    return children;
  }

  if (!isLoaded || (isSignedIn && dbLoading && !dbUser)) {
    return <ModernLoader text="Authenticating" />;
  }

  if (!isSignedIn) {
    return <RedirectToSignIn />;
  }

  // Get role from multiple metadata locations + Database Sync for real-time reactivity
  const clerkRole = 
    clerkUser?.publicMetadata?.role || 
    clerkUser?.unsafeMetadata?.role || 
    clerkUser?.privateMetadata?.role;

  const dbRole = dbUser?.role;

  const isAdmin = 
    clerkRole === "admin" || 
    dbRole === "admin" ||
    clerkUser?.emailAddresses?.some(email => 
      email.emailAddress?.endsWith("@admin.com")
    );
  
  const isPartner = 
    clerkRole === "partner" || 
    clerkRole === "theater_partner" || 
    dbRole === "partner";

  // Access rules:
  // 1. Admin can access everything (admin side and partner side)
  // 2. Partner can only access partner side
  // 3. User can only access user side

  // Shared Styles for Access Denied Pages
  const accessDeniedStyles = `
    .district-access-denied {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: radial-gradient(circle at 50% 50%, #1a1b20 0%, #0a0b0d 100%);
      padding: 24px;
      font-family: 'Inter', system-ui, sans-serif;
    }
    .denied-card {
      max-width: 440px;
      width: 100%;
      padding: 48px 32px;
      border-radius: 24px;
      text-align: center;
      border: 1px solid rgba(255, 255, 255, 0.08);
      background: rgba(255, 255, 255, 0.03);
      backdrop-filter: blur(20px);
      box-shadow: 0 24px 48px rgba(0, 0, 0, 0.4);
    }
    .denied-icon-wrap {
      width: 80px;
      height: 80px;
      border-radius: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 24px;
    }
    .denied-icon-wrap.admin {
      background: rgba(88, 86, 214, 0.1);
      color: #5856d6;
    }
    .denied-icon-wrap.partner {
      background: rgba(255, 159, 10, 0.1);
      color: #ff9f0a;
    }
    .denied-icon-wrap svg {
      width: 40px;
      height: 40px;
    }
    .denied-title {
      color: #fff;
      font-size: 24px;
      font-weight: 700;
      margin-bottom: 16px;
      letter-spacing: -0.02em;
    }
    .denied-text {
      color: rgba(255, 255, 255, 0.6);
      font-size: 15px;
      line-height: 1.6;
      margin-bottom: 32px;
    }
    .denied-actions {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    .denied-btn {
      padding: 14px 24px;
      border-radius: 12px;
      font-size: 15px;
      font-weight: 600;
      transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
      cursor: pointer;
      border: none;
    }
    .denied-btn.primary {
      background: #fff;
      color: #000;
    }
    .denied-btn.primary:hover {
      background: #e5e5e5;
      transform: translateY(-2px);
    }
    .denied-btn.secondary {
      background: rgba(255, 255, 255, 0.05);
      color: #fff;
      border: 1px solid rgba(255, 255, 255, 0.1);
    }
    .denied-btn.secondary:hover {
      background: rgba(255, 255, 255, 0.1);
    }
  `;


  // Admin section: Only Admin
  if (adminOnly && !isAdmin) {
    return (
      <div className="district-access-denied">
        <style>{accessDeniedStyles}</style>
        <div className="denied-card">
          <div className="denied-icon-wrap admin">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M12 15L12 9" strokeLinecap="round" />
              <path d="M3 11L21 11" strokeLinecap="round" />
              <path d="M9 11L9 15" strokeLinecap="round" />
              <path d="M15 11L15 15" strokeLinecap="round" />
              <path d="M3 11L12 3L21 11" strokeLinecap="round" />
              <path d="M21 11V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V11" strokeLinecap="round" />
            </svg>
          </div>
          <h2 className="denied-title">Command Center Restricted</h2>
          <p className="denied-text">
            This workspace is reserved for authorized system administrators. 
            Unauthorized access attempts are logged.
          </p>
          <div className="denied-actions">
            <button className="denied-btn secondary" onClick={() => navigate("/")}>
              Return Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Partner section: Admin or Partner
  if (partnerOnly && !isPartner && !isAdmin) {
    return (
      <div className="district-access-denied">
        <style>{accessDeniedStyles}</style>
        <div className="denied-card">
          <div className="denied-icon-wrap partner">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M19 21V5C19 3.89543 18.1046 3 17 3H7C5.89543 3 5 3.89543 5 5V21" strokeLinecap="round" />
              <path d="M3 21H21" strokeLinecap="round" />
              <path d="M9 7H15" strokeLinecap="round" />
              <path d="M9 11H15" strokeLinecap="round" />
              <path d="M9 15H15" strokeLinecap="round" />
            </svg>
          </div>
          <h2 className="denied-title">Partner Hub Required</h2>
          <p className="denied-text">
            To list shows and manage your cinema, you need to be registered as a Theater Partner. 
            Join our network of cinema operators today.
          </p>
          <div className="denied-actions">
            <button className="denied-btn primary" onClick={() => navigate("/partner/onboarding")}>
              Become a Partner
            </button>
            <button className="denied-btn secondary" onClick={() => navigate("/")}>
              Return to Movies
            </button>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default ClerkProtectedRoute;
